import React, { useEffect, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { transitService, RetrogradeInfo } from '../../services/transitApi';

const PLANET_ICON: Record<string, string> = {
  'Mercúrio': '☿',
  'Vênus': '♀',
  'Marte': '♂',
  'Júpiter': '♃',
  'Saturno': '♄',
  'Urano': '♅',
  'Netuno': '♆',
  'Plutão': '♇',
};

const formatDate = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

const RetrogradeItem: React.FC<{ info: RetrogradeInfo }> = ({ info }) => {
  const [expanded, setExpanded] = useState(false);
  const icon = PLANET_ICON[info.planet] || '✦';

  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center bg-amber-500/15 text-amber-300 text-sm font-bold">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-white">{info.planet} Retrógrado</h4>
        <p className="text-xs text-gray-400 mb-1">
          De {formatDate(info.start_date)} a {formatDate(info.end_date)} · em {info.sign}
        </p>
        {info.house_note && (
          <p className="text-xs text-amber-300/80 mb-1">{info.house_note}</p>
        )}
        <p className="text-xs text-gray-300 leading-relaxed">
          {expanded ? info.text : `${info.text.slice(0, 90)}${info.text.length > 90 ? '…' : ''}`}
          {info.text.length > 90 && (
            <button
              onClick={() => setExpanded(v => !v)}
              className="ml-1 text-gold-400 hover:text-gold-300 font-semibold"
            >
              {expanded ? 'Ler menos' : 'Ler mais'}
            </button>
          )}
        </p>
      </div>
    </div>
  );
};

const RetrogradosAtivos: React.FC = () => {
  const [data, setData] = useState<{ available: boolean; retrogrades: RetrogradeInfo[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    transitService.getMyRetrogrades()
      .then(setData)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  // Sem Swiss Ephemeris disponível no ambiente — não é um estado que o usuário
  // deveria ver (só acontece em dev local sem a lib nativa instalada).
  if (!loading && !error && data && !data.available) return null;

  return (
    <div
      className="backdrop-blur-sm rounded-xl p-6 border border-white/20 flex flex-col lg:flex-1 lg:min-h-0"
      style={{ background: 'rgba(255,255,255,0.05)' }}
    >
      <div className="flex items-center space-x-2 mb-4 flex-shrink-0">
        <ArrowPathIcon className="w-6 h-6 text-amber-300" />
        <h3 className="text-lg font-semibold text-white">Retrógrados Ativos</h3>
      </div>

      <div className="lg:flex-1 lg:min-h-0 overflow-y-auto hide-scrollbar pr-1 flex flex-col">
        {/* my-auto: quando o conteúdo é mais curto que o box, as margens automáticas
            dividem o espaço sobrando e centralizam o bloco (em vez de ficar colado no
            topo com espaço vazio embaixo). Quando o conteúdo é maior que o box e
            precisa rolar, as margens automáticas simplesmente colapsam a 0 e o
            alinhamento volta ao normal — funciona nos dois casos sem CSS exótico. */}
        {loading ? (
          <p className="text-gray-400 text-sm my-auto">Verificando os céus…</p>
        ) : error ? (
          <p className="text-red-300 text-sm my-auto">Não foi possível carregar os retrógrados agora.</p>
        ) : !data || data.retrogrades.length === 0 ? (
          <p className="text-gray-400 text-sm my-auto">✨ Nenhum planeta retrógrado agora — céu livre para agir e avançar.</p>
        ) : (
          <div className="space-y-4 my-auto">
            {data.retrogrades.map(info => (
              <RetrogradeItem key={info.planet} info={info} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RetrogradosAtivos;
